// src/admin/CreateUser/Modal/ModalUpdatePermisosEventSport/ModalUpdatePermisosEventSport.jsx
import React, { useEffect, useState } from "react";
import Modal from "@mui/joy/Modal";
import Typography from "@mui/joy/Typography";
import { useSelector } from "react-redux";
import axios from "axios";
import Select from "react-select";
import { toast } from "react-hot-toast";
import { MdDelete } from "react-icons/md"; // Ícono de eliminación
import API_BASE_URL from "../../../../config/config.js";

const ModalUpdatePermisosEventSport = ({ open, setOpen, user }) => {
    const yearSelected = useSelector((state) => state.year.yearSelected);
    const [data, setData] = useState([]); // Eventos y deportes permitidos existentes
    const [allEvents, setAllEvents] = useState([]); // Todos los eventos disponibles
    const [allSports, setAllSports] = useState([]); // Todos los deportes disponibles
    const [selectedEvent, setSelectedEvent] = useState(null); // Evento seleccionado
    const [selectedSport, setSelectedSport] = useState(null); // Deporte seleccionado
    const [isSpecific, setIsSpecific] = useState(false); // Inicializar isSpecific

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Obtener eventos permitidos según el rol
                if (user.rol === 2 || user.rol === 3) {
                    const eventSportResponse = await axios.post(
                        `${API_BASE_URL}/menu_championships_sports`,
                        {  
                            iduser: user.iduser,
                            periodo: Number(yearSelected),
                            tipo: 3,
                        }
                    );

                    if (user.rol === 3) {
                        setIsSpecific(true); // Configurar isSpecific si el rol es 3
                    }

                    setData(eventSportResponse.data.championships); // Eventos permitidos
                }

                // Obtener todos los eventos disponibles
                const championshipsResponse = await axios.get(
                    `${API_BASE_URL}/championships?periodo=${yearSelected}`
                );
                setAllEvents(
                    championshipsResponse.data.map((event) => ({
                        value: event.idchampionship,
                        label: event.name,
                    }))
                );

                // Obtener todos los deportes disponibles solo si es rol 3
                if (user.rol === 3) {
                    const sportsResponse = await axios.get(`${API_BASE_URL}/sports`);
                    setAllSports(
                        sportsResponse.data.map((sport) => ({
                            value: sport.idsport,
                            label: sport.name,
                        }))
                    );
                }
            } catch (error) {
                console.error("Error al traer datos:", error);
            }
        };

        if (user && user.iduser && yearSelected) {
            fetchData();
        }
    }, [user, yearSelected]);

    const handleAddEventSport = async () => {
        if (!selectedEvent || (user.rol === 3 && !selectedSport)) {
            toast.error("Por favor, selecciona todos los campos necesarios antes de agregar.");
            return;
        }

        const payload = {
            iduser: user.iduser,
            idchampionship: selectedEvent.value,
            idsport: selectedSport?.value || null, // Solo se envía si hay deporte seleccionado
        };

        try {
            await axios.post(`${API_BASE_URL}/menu_create_championship_sport`, payload);
            toast.success("Permiso agregado exitosamente.");

            // Actualizar la lista de permisos después de agregar uno nuevo
            setData((prevData) => {
                const updatedData = [...prevData];
                const existingChampionshipIndex = updatedData.findIndex(
                    (item) => item.idchampionship === selectedEvent.value
                );

                if (existingChampionshipIndex >= 0 && user.rol === 3) {
                    updatedData[existingChampionshipIndex].sports.push({
                        idsport: selectedSport.value,
                        name: selectedSport.label,
                    });
                } else if (existingChampionshipIndex === -1) {
                    updatedData.push({
                        idchampionship: selectedEvent.value,
                        name: selectedEvent.label,
                        sports: user.rol === 3 ? [{ idsport: selectedSport.value, name: selectedSport.label }] : [],
                    });
                }
                return updatedData;
            });

            setSelectedEvent(null);
            setSelectedSport(null);
        } catch (error) {
            console.error("Error al agregar permiso:", error);
            toast.error("Ocurrió un error al agregar el permiso.");
        }
    };

    const handleDelete = async (idchampionship, idsport = null) => {
        const payload = {
            iduser: user.iduser,
            idchampionship,
            idsport, // Enviar solo si está presente
        };

        try {
            await axios.delete(`${API_BASE_URL}/menu_delete_championship_sport`, { data: payload });
            toast.success("Permiso eliminado exitosamente.");

            // Actualizar la lista después de eliminar
            setData((prevData) =>
                prevData.map((championship) => {
                    if (championship.idchampionship === idchampionship) {
                        if (idsport) {
                            // Eliminar deporte del campeonato
                            return {
                                ...championship,
                                sports: championship.sports.filter((sport) => sport.idsport !== idsport),
                            };
                        } else {
                            // Eliminar todo el campeonato
                            return null;
                        }
                    }
                    return championship;
                }).filter(Boolean) // Elimina los nulos
            );
        } catch (error) {
            console.error("Error al eliminar permiso:", error);
            toast.error("Ocurrió un error al eliminar el permiso.");
        }
    };

    return (
        <Modal
            aria-labelledby="modal-title"
            aria-describedby="modal-desc"
            open={open}
            onClose={() => setOpen(false)}
            sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
        >
            <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-lg relative">
                <button
                    className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 focus:outline-none"
                    onClick={() => setOpen(false)}
                >
                    ✖
                </button>
                <Typography
                    component="h2"
                    id="modal-title"
                    className="text-xl font-bold text-gray-800 mb-4 text-center"
                >
                    {user.rol === 1 ? "Permiso de Admin" : "Permisos de Eventos"}
                </Typography>

                {user.rol === 1 ? (
                    <div className="text-center">
                        <p className="text-gray-700 text-lg">Acceso a todos los eventos y deportes</p>
                    </div>
                ) : (
                    <>
                        <div className="space-y-4">
                            <div className="flex items-center space-x-3">
                                <Select
                                    options={allEvents}
                                    value={selectedEvent}
                                    onChange={(option) => setSelectedEvent(option)}
                                    placeholder="Selecciona un evento"
                                    className="w-full"
                                />
                            </div>

                            {user.rol === 3 && (
                                <div className="flex items-center space-x-3">
                                    <Select
                                        options={allSports}
                                        value={selectedSport}
                                        onChange={(option) => setSelectedSport(option)}
                                        placeholder="Selecciona un deporte"
                                        className="w-full"
                                    />
                                </div>
                            )}

                            <div className="flex justify-end">
                                <button
                                    className="bg-blue-500 text-white px-4 py-2 rounded shadow hover:bg-blue-600 focus:outline-none"
                                    onClick={handleAddEventSport}
                                >
                                    Agregar
                                </button>
                            </div>
                        </div>

                        <div className="mt-4">
                            <h3 className="text-lg font-semibold text-gray-700 mb-2">
                                {user.rol === 2 ? "Eventos permitidos" : "Eventos y deportes permitidos"}
                            </h3>
                            {data.map((championship) => (
                                <div key={championship.idchampionship} className="border-b pb-4">
                                    <div className="flex justify-between items-center">
                                        <h3 className="text-lg font-semibold text-gray-700">{championship.name}</h3>
                                        {user.rol === 2 && (
                                            <MdDelete
                                                className="text-red-500 cursor-pointer"
                                                onClick={() => handleDelete(championship.idchampionship)}
                                            />
                                        )}
                                    </div>
                                    {isSpecific && championship.sports && (
                                        <ul className="list-disc pl-5">
                                            {championship.sports.map((sport) => (
                                                <li key={sport.idsport} className="flex justify-between items-center">
                                                    <span className="text-gray-700">{sport.name}</span>
                                                    <MdDelete
                                                        className="text-red-500 cursor-pointer"
                                                        onClick={() =>
                                                            handleDelete(championship.idchampionship, sport.idsport)
                                                        }
                                                    />
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
};

export default ModalUpdatePermisosEventSport;
